import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import {
    CheckOutlined,
    CloseOutlined,
    PushpinFilled,
    SearchOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { chatApi } from '@/api';
import { useAddAlert } from '@/hooks/useAddAlert';
import { usePaginatedApiSearch } from '@/hooks/usePaginatedApiSearch';
import Button from '@UI/New/Button';
import Preloader from '@UI/Preloader';
import { getAvatar, getRightWord } from '@common/Chat/helpers';
import s from './ForwardMessagesModal.module.css';

const MAX_SELECTED_CHATS = 20;
const CHATS_PER_PAGE = 20;

const getMessagePreview = (message) => {
    const rawMessage = message?.message;
    const text = typeof rawMessage === 'string'
        ? rawMessage.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
        : '';

    if (text) return text;

    const firstFile = Array.isArray(message?.files) ? message.files[0] : null;
    const fileType = String(firstFile?.type || firstFile?.mime_type || '');

    if (fileType.includes('audio')) return 'Голосовое сообщение';
    if (fileType.includes('image')) return 'Изображение';
    if (firstFile) return firstFile?.original || firstFile?.name || 'Файл';

    return 'Сообщение';
};

const getErrorText = (error) => {
    const value =
        error?.response?.data?.error ??
        error?.response?.data?.message ??
        error?.data?.error ??
        error?.data?.message ??
        error?.message;

    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'string' && value.trim()) return value;

    return 'Не удалось переслать сообщение';
};

const ForwardMessagesModal = ({ message, onClose, onForwarded }) => {
    const addAlert = useAddAlert();
    const [selectedChats, setSelectedChats] = useState([]);
    const selectedChatsRef = useRef([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const loadAvailableChats = useCallback(({ search, paramsPagination }, signal) => {
        const preparedSearch = search?.trim();

        return chatApi.getAvailableForForward({
            page: paramsPagination?.page ?? 1,
            'per-page': paramsPagination?.perPage ?? CHATS_PER_PAGE,
            ...(preparedSearch ? { search: preparedSearch } : {}),
        }, signal);
    }, []);

    const {
        data: chats,
        loading,
        loadingMore,
        hasMore,
        searchQuery,
        setSearchQuery,
        loadMore,
    } = usePaginatedApiSearch({
        apiMethod: loadAvailableChats,
        responseKey: 'data',
        debounceDelay: 350,
        perPage: CHATS_PER_PAGE,
    });

    const selectedChatIds = useMemo(
        () => new Set(selectedChats.map((chat) => String(chat.id))),
        [selectedChats],
    );

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !isSubmitting) onClose();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isSubmitting, onClose]);

    const toggleChat = (chat) => {
        const chatId = String(chat.id);
        const current = selectedChatsRef.current;

        if (current.some((item) => String(item.id) === chatId)) {
            const next = current.filter((item) => String(item.id) !== chatId);
            selectedChatsRef.current = next;
            setSelectedChats(next);
            return;
        }

        if (current.length >= MAX_SELECTED_CHATS) {
            addAlert({
                type: 'error',
                title: `Можно выбрать не больше ${MAX_SELECTED_CHATS} чатов`,
            });
            return;
        }

        const next = [...current, chat];
        selectedChatsRef.current = next;
        setSelectedChats(next);
    };

    const handleScroll = (event) => {
        const element = event.currentTarget;

        if (element.scrollHeight - element.scrollTop - element.clientHeight < 80) {
            loadMore();
        }
    };

    const handleForward = async () => {
        if (!message?.id || !selectedChats.length || isSubmitting) return;

        setIsSubmitting(true);

        try {
            const response = await chatApi.forwardMessages({
                message_id: message.id,
                chat_ids: selectedChats.map((chat) => chat.id),
            });
            const responseData = response?.data;

            if (response?.response || responseData?.status !== 'success') {
                throw response;
            }

            const forwarded = Array.isArray(responseData?.data?.forwarded)
                ? responseData.data.forwarded
                : [];

            onForwarded?.(forwarded);
            selectedChatsRef.current = [];
            setSelectedChats([]);
            onClose();
            addAlert({
                title: 'Сообщение переслано',
                text: `В ${selectedChats.length} ${getRightWord(selectedChats.length, ['чат', 'чата', 'чатов'])}`,
            });
        } catch (error) {
            addAlert({
                type: 'error',
                title: 'Не удалось переслать сообщение',
                text: getErrorText(error),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className={s.overlay}
            role="dialog"
            aria-modal="true"
            aria-labelledby="forward-message-title"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !isSubmitting) onClose();
            }}
        >
            <div className={s.modal} onMouseDown={(event) => event.stopPropagation()}>
                <div className={s.header}>
                    <div>
                        <h2 id="forward-message-title">Переслать сообщение</h2>
                        <p>{getMessagePreview(message)}</p>
                    </div>
                    <button
                        type="button"
                        className={s.closeButton}
                        aria-label="Закрыть"
                        disabled={isSubmitting}
                        onClick={onClose}
                    >
                        <CloseOutlined />
                    </button>
                </div>

                <label className={s.search}>
                    <SearchOutlined />
                    <input
                        autoFocus
                        value={searchQuery}
                        placeholder="Найти чат"
                        onChange={(event) => setSearchQuery(event.target.value)}
                    />
                </label>

                <div className={s.selectionHeader}>
                    <span>Выбрано: {selectedChats.length} из {MAX_SELECTED_CHATS}</span>
                    {!!selectedChats.length && (
                        <button
                            type="button"
                            onClick={() => {
                                selectedChatsRef.current = [];
                                setSelectedChats([]);
                            }}
                        >
                            Очистить
                        </button>
                    )}
                </div>

                {!!selectedChats.length && (
                    <div className={classNames('new-design-scroll', s.selectedChats)}>
                        {selectedChats.map((chat) => (
                            <button
                                key={chat.id}
                                type="button"
                                className={s.selectedChat}
                                title={chat.name}
                                onClick={() => toggleChat(chat)}
                            >
                                <span>{chat.name}</span>
                                <CloseOutlined />
                            </button>
                        ))}
                    </div>
                )}

                <div
                    className={classNames('new-design-scroll', s.chatList)}
                    onScroll={handleScroll}
                >
                    {loading && !chats.length && (
                        <div className={s.state}><Preloader /></div>
                    )}

                    {!loading && !chats.length && (
                        <div className={s.state}>Доступные чаты не найдены</div>
                    )}

                    {chats.map((chat) => {
                        const isSelected = selectedChatIds.has(String(chat.id));

                        return (
                            <button
                                key={chat.id}
                                type="button"
                                className={classNames(s.chatRow, isSelected && s.chatRowSelected)}
                                aria-pressed={isSelected}
                                onClick={() => toggleChat(chat)}
                            >
                                <span className={s.avatar}>
                                    {chat.avatar || chat.type !== 'group' ? (
                                        <img src={getAvatar(chat.avatar)} alt="" />
                                    ) : (
                                        <TeamOutlined />
                                    )}
                                </span>
                                <span className={s.chatInfo}>
                                    <span className={s.chatName}>{chat.name || 'Чат'}</span>
                                    <span className={s.chatType}>
                                        {chat.type === 'group'
                                            ? `${chat.membersCount ?? 0} ${getRightWord(chat.membersCount ?? 0, ['участник', 'участника', 'участников'])}`
                                            : 'Личный чат'}
                                    </span>
                                </span>
                                {chat.isFixed && <PushpinFilled className={s.fixedIcon} title="Закрепленный чат" />}
                                <span className={classNames(s.checkbox, isSelected && s.checkboxSelected)}>
                                    {isSelected && <CheckOutlined />}
                                </span>
                            </button>
                        );
                    })}

                    {loadingMore && <div className={s.loadingMore}><Preloader /></div>}
                    {!hasMore && !!chats.length && <div className={s.listEnd}>Все доступные чаты загружены</div>}
                </div>

                <div className={s.footer}>
                    <Button color="white" disabled={isSubmitting} onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        isLoading={isSubmitting}
                        disabled={!selectedChats.length || isSubmitting}
                        onClick={handleForward}
                    >
                        Переслать{selectedChats.length ? ` (${selectedChats.length})` : ''}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ForwardMessagesModal;
